import React, { useEffect, useState } from 'react';
import { Form, Select, Input, InputNumber, Switch, Row, Col, message } from 'antd';
import { masterDataService, Vendor, VendorProductCatalogItem, Product } from '../services/masterDataService';
import ModernFormModal from './ModernFormModal';

export type VendorProductLinkMode = 'add' | 'edit' | 'view';

interface Props {
  open: boolean;
  mode: VendorProductLinkMode;
  row: VendorProductCatalogItem | null;
  vendors: Vendor[];
  onClose: () => void;
  onSuccess: () => void;
  /** When adding from a company-master-only catalog row, pre-select this product after vendor is chosen. */
  prefillProductId?: number | null;
}

const VendorProductLinkFormModal: React.FC<Props> = ({
  open,
  mode,
  row,
  vendors,
  onClose,
  onSuccess,
  prefillProductId,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [linkProducts, setLinkProducts] = useState<Product[]>([]);
  const [vendorId, setVendorId] = useState<number | undefined>();

  const readOnly = mode === 'view';
  const isAdd = mode === 'add';
  const isEdit = mode === 'edit';

  const addVendorIdWatch = Form.useWatch('vendor_id', form);
  const effectiveVendorIdForSummary = isAdd ? addVendorIdWatch : row?.vendor.id;

  useEffect(() => {
    if (!open) return;
    if (isAdd) {
      form.resetFields();
      form.setFieldsValue({
        vendor_price: 0,
        lead_time_days: 7,
        minimum_order_quantity: 1,
        is_preferred: false,
        unit_of_measure: 'pcs',
      });
      setVendorId(undefined);
      setLinkProducts([]);
      return;
    }
    if (row) {
      form.setFieldsValue({
        name: row.name,
        description: row.description ?? '',
        unit_of_measure: row.unit_of_measure || 'pcs',
        product_id: row.product?.id,
        vendor_product_code: row.vendor_product_code ?? '',
        vendor_price: row.vendor_price,
        lead_time_days: row.lead_time_days,
        minimum_order_quantity: row.minimum_order_quantity,
        is_preferred: row.is_preferred,
      });
      setVendorId(row.vendor.id);
    }
  }, [open, isAdd, row, form]);

  useEffect(() => {
    if (!open || !vendorId) {
      if (isAdd && !vendorId) setLinkProducts([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const [all, linked] = await Promise.all([
          masterDataService.getProducts({ active_only: true, limit: 2000 }),
          masterDataService.getVendorProducts(vendorId),
        ]);
        if (cancelled) return;
        const taken = new Set(
          (linked || []).map((l) => l.product_id).filter((id): id is number => id != null),
        );
        if (isAdd) {
          setLinkProducts((all || []).filter((p) => !taken.has(p.id)));
        } else {
          setLinkProducts(all || []);
        }
      } catch {
        setLinkProducts([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, vendorId, isAdd]);

  useEffect(() => {
    if (!open || !isAdd || prefillProductId == null || !vendorId) return;
    if (linkProducts.some((p) => p.id === prefillProductId)) {
      form.setFieldsValue({ product_id: prefillProductId });
    }
  }, [open, isAdd, prefillProductId, vendorId, linkProducts, form]);

  useEffect(() => {
    if (isAdd && addVendorIdWatch != null) {
      setVendorId(addVendorIdWatch);
    }
  }, [isAdd, addVendorIdWatch]);

  const handleFinish = async (v: Record<string, unknown>) => {
    if (readOnly) return;
    setLoading(true);
    try {
      if (isAdd) {
        await masterDataService.addVendorProduct(v.vendor_id as number, {
          name: v.name as string,
          description: (v.description as string) || undefined,
          unit_of_measure: (v.unit_of_measure as string) || 'pcs',
          product_id: (v.product_id as number) ?? undefined,
          vendor_product_code: v.vendor_product_code as string,
          vendor_price: v.vendor_price as number,
          lead_time_days: v.lead_time_days as number,
          minimum_order_quantity: v.minimum_order_quantity as number,
          is_preferred: v.is_preferred as boolean,
        });
        message.success('Vendor product created successfully!');
      } else if (row) {
        await masterDataService.updateVendorProduct(row.vendor.id, row.id, {
          name: v.name as string,
          description: (v.description as string) || undefined,
          unit_of_measure: (v.unit_of_measure as string) || 'pcs',
          product_id: (v.product_id as number) ?? null,
          vendor_product_code: (v.vendor_product_code as string) || undefined,
          vendor_price: v.vendor_price as number,
          lead_time_days: v.lead_time_days as number,
          minimum_order_quantity: v.minimum_order_quantity as number,
          is_preferred: v.is_preferred as boolean,
        });
        message.success('Vendor product updated successfully!');
      }
      onSuccess();
      onClose();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: unknown } } };
      const d = err?.response?.data?.detail;
      let msg = 'Save failed';
      if (typeof d === 'string') msg = d;
      else if (Array.isArray(d) && d[0] && typeof (d[0] as { msg?: string }).msg === 'string') msg = (d[0] as { msg: string }).msg;
      message.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const title =
    mode === 'add' ? 'Add New Vendor Product' : mode === 'edit' ? 'Edit Vendor Product' : 'View Vendor Product';

  const subtitle =
    mode === 'add'
      ? 'Fill in the details below to add a new vendor product.'
      : mode === 'edit'
        ? 'Fill in the details below to update this vendor product.'
        : 'Review the vendor product details below.';

  const vendorForSummary = effectiveVendorIdForSummary
    ? vendors.find((x) => x.id === effectiveVendorIdForSummary)
    : undefined;

  const headerSummary =
    vendorForSummary || row ? (
      <>
        <div>
          <span className="mf-summary-label">Vendor:</span>{' '}
          <span className="mf-summary-value">
            {vendorForSummary ? `${vendorForSummary.name} (${vendorForSummary.code})` : row ? `${row.vendor.name} (${row.vendor.code})` : '—'}
          </span>
        </div>
        {row && (isEdit || readOnly) ? (
          <div>
            <span className="mf-summary-label">Vendor product:</span>{' '}
            <span className="mf-summary-value">{row.name}</span>
          </div>
        ) : null}
      </>
    ) : undefined;

  return (
    <ModernFormModal
      open={open}
      onClose={onClose}
      title={title}
      subtitle={subtitle}
      headerVariant="light"
      headerSummary={headerSummary}
      getContainer={() => document.querySelector('.main-content-full') || document.body}
      wrapClassName="form-panel-full-wrap"
      width="100%"
      style={{ maxWidth: 'none' }}
      bodyStyle={{ padding: 0 }}
      cancelText={readOnly ? 'Close' : 'Cancel'}
      okText={isAdd ? 'Create' : 'Update'}
      onOk={readOnly ? undefined : () => form.submit()}
      isSubmitting={loading}
      showOkButton={!readOnly}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        requiredMark={!readOnly}
        autoComplete="off"
        onFinish={handleFinish}
        style={{ width: '100%' }}
      >
        {isAdd && (
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item name="vendor_id" label="Vendor" rules={[{ required: true, message: 'Select a vendor' }]}>
                <Select
                  placeholder="Select vendor"
                  showSearch
                  optionFilterProp="label"
                  onChange={(id: number) => {
                    setVendorId(id);
                    form.setFieldsValue({ product_id: undefined });
                  }}
                  options={vendors.map((v) => ({
                    value: v.id,
                    label: `${v.name} (${v.code})`,
                  }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="name"
                label="Vendor product name"
                rules={[{ required: true, message: 'Enter the name as the supplier lists it' }]}
                tooltip="How this vendor refers to the item (separate from your company product name)."
              >
                <Input placeholder="e.g. Cotton Gho — Blue" disabled={readOnly} />
              </Form.Item>
            </Col>
          </Row>
        )}

        {!isAdd && (
          <Form.Item name="name" label="Vendor product name" rules={[{ required: !readOnly, message: 'Enter name' }]}>
            <Input disabled={readOnly} />
          </Form.Item>
        )}

        <Form.Item name="description" label="Description">
          <Input.TextArea rows={2} disabled={readOnly} placeholder="Optional" />
        </Form.Item>

        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item name="unit_of_measure" label="Unit of measure" initialValue="pcs">
              <Input placeholder="pcs" disabled={readOnly} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="product_id"
              label="Link to company product (optional)"
              tooltip="When set, received goods update this inventory item. Leave empty for vendor-only lines until you map them."
            >
              <Select
                placeholder={vendorId ? 'No link' : isAdd ? 'Choose a vendor first' : 'No link'}
                allowClear
                showSearch
                optionFilterProp="label"
                disabled={readOnly || (isAdd && !vendorId)}
                options={linkProducts.map((p) => ({
                  value: p.id,
                  label: `${p.name} (${p.code})`,
                }))}
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="vendor_product_code"
          label="Vendor SKU"
          tooltip="Supplier catalog or internal code for this item on purchase orders."
        >
          <Input placeholder="e.g. supplier catalog code" disabled={readOnly} autoComplete="off" />
        </Form.Item>

        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              name="vendor_price"
              label="Vendor price"
              rules={[{ required: !readOnly, message: 'Please enter vendor price' }]}
              tooltip="What you pay this supplier for one unit (before your selling price)."
            >
              <InputNumber<number>
                min={0}
                disabled={readOnly}
                placeholder="0.00"
                style={{ width: '100%' }}
                formatter={(value) => `Nu. ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={(value) => {
                  const raw = (value || '').replace(/Nu.\s?|(,*)/g, '');
                  const n = parseFloat(raw);
                  return Number.isNaN(n) ? 0 : n;
                }}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="minimum_order_quantity"
              label="MOQ (minimum order quantity)"
              rules={[{ required: !readOnly, message: 'Please enter MOQ' }]}
            >
              <InputNumber min={1} placeholder="1" style={{ width: '100%' }} disabled={readOnly} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              name="lead_time_days"
              label="Lead time (days)"
              rules={[{ required: !readOnly, message: 'Please enter lead time' }]}
            >
              <InputNumber min={0} placeholder="7" style={{ width: '100%' }} disabled={readOnly} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="is_preferred"
              label="Preferred supplier for linked product"
              valuePropName="checked"
              tooltip="When linked to a company product, marks this vendor as preferred for that item."
            >
              <Switch disabled={readOnly} checkedChildren="Yes" unCheckedChildren="No" />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </ModernFormModal>
  );
};

export default VendorProductLinkFormModal;
